import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  Snackbar,
  Divider,
  Tabs,
  Tab,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import MeetingConfirmationTab from '../components/MeetingConfirmationTab';
import { businessConfigService } from '../services/businessConfigService';
import { ROUTES } from '../constants/routes';

const DAYS_OF_WEEK = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const GRANULARITY_OPTIONS = [15, 30, 45, 60];

/**
 * Calendar Configuration Page - Edit calendar settings, meeting types, availability
 */
const CalendarConfigPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedCalendar, setSelectedCalendar] = useState(0);
  const [calendars, setCalendars] = useState([]);
  const [notificationTemplates, setNotificationTemplates] = useState({});
  const [notificationLanguage, setNotificationLanguage] = useState('pl');
  const [availablePlaceholders, setAvailablePlaceholders] = useState([]);
  const [confirmationConfig, setConfirmationConfig] = useState(null);
  const [globalConfig, setGlobalConfig] = useState(null);
  
  // Section-specific override states (true = use calendar override, false = use global)
  const [generalOverride, setGeneralOverride] = useState(false);
  const [meetingTypesOverride, setMeetingTypesOverride] = useState(false);
  const [remindersOverride, setRemindersOverride] = useState(false);
  const [businessHoursOverride, setBusinessHoursOverride] = useState(false);
  const [bookingSettingsOverride, setBookingSettingsOverride] = useState(false);
  const [notificationsOverride, setNotificationsOverride] = useState(false);
  const [confirmationOverride, setConfirmationOverride] = useState(false);
  
  // Helper to check if we're viewing global settings
  const isGlobalView = selectedCalendar === -1;
  
  // Helper to get current data source (global or calendar)
  const currentData = isGlobalView ? (globalConfig || {}) : (calendars[selectedCalendar] || {});
  const currentCalendar = isGlobalView ? (globalConfig || {}) : (calendars[selectedCalendar] || {});
  
  // Helper to get reminder times based on view
  const getReminderTimes = () => {
    if (isGlobalView) {
      return globalConfig?.reminders?.default_times || [];
    }
    // Read from nested reminder_settings.times structure
    return calendars[selectedCalendar]?.reminder_settings?.times || [];
  };
  
  // Meeting type dialog state
  const [meetingTypeDialog, setMeetingTypeDialog] = useState({
    open: false,
    isEdit: false,
    editKey: '',
    name: '',
    duration: '30',
    goal: [],
    description: '',
    goalInput: '',
  });

  // Reminder time dialog state
  const [reminderDialog, setReminderDialog] = useState({
    open: false,
    minutes: 60,
  });

  useEffect(() => {
    const fetchCalendars = async () => {
      try {
        setLoading(true);
        
        // Load global configuration first
        await loadGlobalConfig();
        
        // Then load calendars
        const calendarList = await businessConfigService.getBusinessCalendars(customerId);
        setCalendars(calendarList);
        
        // Start with global view (index -1)
        setSelectedCalendar(-1);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchCalendars();
    }
  }, [customerId]);

  // Load notification templates and confirmation config when selected calendar changes
  useEffect(() => {
    if (isGlobalView) {
      // Load global configuration
      loadGlobalConfig();
      // Reset all override states when viewing global
      setGeneralOverride(false);
      setMeetingTypesOverride(false);
      setRemindersOverride(false);
      setBusinessHoursOverride(false);
      setBookingSettingsOverride(false);
      setNotificationsOverride(false);
      setConfirmationOverride(false);
    } else if (calendars.length > 0 && calendars[selectedCalendar]) {
      // Load calendar-specific configuration
      loadNotificationTemplates(calendars[selectedCalendar].name);
      loadConfirmationConfig(calendars[selectedCalendar].name);
      
      // Initialize override states from calendar data
      const calendar = calendars[selectedCalendar];
      setGeneralOverride(calendar.general?.override_global || false);
      setMeetingTypesOverride(calendar.meeting_types_override?.override_global || false);
      setRemindersOverride(calendar.reminders?.override_global || false);
      setBusinessHoursOverride(calendar.business_hours?.override_global || false);
      setBookingSettingsOverride(calendar.booking_settings?.override_global || false);
      setNotificationsOverride(calendar.notifications?.override_global || false);
      setConfirmationOverride(calendar.confirmation_config?.override_global || false);
    }
  }, [selectedCalendar, calendars.length]);

  const loadNotificationTemplates = async (calendarName) => {
    try {
      const { templates, placeholders } = await businessConfigService.getNotificationTemplates(
        customerId,
        calendarName
      );
      setNotificationTemplates(templates);
      setAvailablePlaceholders(placeholders);
    } catch (err) {
      console.error('Error loading notification templates:', err);
      // Set defaults if loading fails
      setNotificationTemplates({});
      setAvailablePlaceholders(['date', 'time', 'business_name', 'calendar_name', 'customer_name', 'meeting_type']);
    }
  };

  const loadGlobalConfig = async () => {
    try {
      const { config } = await businessConfigService.getGlobalSettings(customerId);
      
      // Global config keeps its nested structure
      // Just ensure sections exist
      const normalizedConfig = {
        ...config,
        general: config.general || {
          granularity: 30
        },
        business_hours: config.business_hours || {
          timezone: 'Europe/Warsaw',
          availability_rules: {}
        },
        confirmation: config.confirmation,
        meeting_types: config.meeting_types || {},
        reminder_times: config.reminders?.default_times || [],
        scheduling_strategy: config.booking_settings?.scheduling_strategy || 'first_available'
      };
      
      setGlobalConfig(normalizedConfig);
      setConfirmationConfig(normalizedConfig?.confirmation);
      
      // Load notification templates from global settings
      const globalNotificationTemplates = config.notifications?.templates || {};
      setNotificationTemplates(globalNotificationTemplates);
      setAvailablePlaceholders(['date', 'time', 'business_name', 'calendar_name', 'customer_name', 'meeting_type']);
    } catch (err) {
      console.error('Error loading global config:', err);
      // Set defaults if loading fails
      const defaultConfig = {
        confirmation: {
          enabled: false,
          method: 'sms',
          hours_before_event: 24,
          max_retry_attempts: 3,
          retry_interval_hours: 4,
          auto_cancel_on_timeout: false,
          language: 'pl',
          templates: {
            sms: {
              pl: 'Dzień dobry! Prosimy o potwierdzenie wizyty w dniu {date} o godzinie {time}. Odpowiedz TAK lub NIE.',
              en: 'Hello! Please confirm your appointment on {date} at {time}. Reply YES or NO.'
            },
            voice: {
              pl: 'Dzień dobry. Prosimy o potwierdzenie wizyty w {business_name} w dniu {date} o godzinie {time}. Naciśnij 1 aby potwierdzić, 2 aby odwołać.',
              en: 'Hello. Please confirm your appointment at {business_name} on {date} at {time}. Press 1 to confirm, 2 to cancel.'
            }
          }
        },
        business_hours: {
          timezone: 'Europe/Warsaw',
          availability_rules: {}
        },
        reminder_times: [],
        meeting_types: {},
        granularity: 15
      };
      setGlobalConfig(defaultConfig);
      setConfirmationConfig(defaultConfig.confirmation);
    }
  };

  const loadConfirmationConfig = async (calendarName) => {
    try {
      const { config } = await businessConfigService.getConfirmationConfig(customerId, calendarName);
      setConfirmationConfig(config);
    } catch (err) {
      console.error('Error loading confirmation config:', err);
      // Fall back to global config
      if (globalConfig?.confirmation) {
        setConfirmationConfig(globalConfig.confirmation);
      }
    }
  };

  // Helper function to sync data from global settings to calendar
  const syncFromGlobal = (section) => {
    if (!globalConfig || isGlobalView) return;
    
    const updated = [...calendars];
    const calendar = {...updated[selectedCalendar]};
    
    switch(section) {
      case 'general':
        calendar.general = {
          ...globalConfig.general,
          override_global: false
        };
        break;
      case 'meeting_types':
        calendar.meeting_types = {...globalConfig.meeting_types};
        calendar.meeting_types_override = { override_global: false };
        break;
      case 'reminders':
        calendar.reminder_times = [...(globalConfig.reminders?.default_times || [])];
        if (!calendar.reminders) calendar.reminders = {};
        calendar.reminders.override_global = false;
        break;
      case 'business_hours':
        calendar.business_hours = {
          ...globalConfig.business_hours,
          override_global: false
        };
        break;
      case 'booking_settings':
        calendar.booking_settings = {
          ...globalConfig.booking_settings,
          override_global: false
        };
        break;
      case 'notifications':
        // Notifications are handled separately via templates
        if (!calendar.notifications) calendar.notifications = {};
        calendar.notifications.override_global = false;
        break;
      case 'confirmation':
        calendar.confirmation_config = {
          ...globalConfig.confirmation,
          override_global: false
        };
        setConfirmationConfig(calendar.confirmation_config);
        break;
    }
    
    updated[selectedCalendar] = calendar;
    setCalendars(updated);
  };

  const handleCalendarChange = (field, value) => {
    const updated = [...calendars];
    updated[selectedCalendar] = {
      ...updated[selectedCalendar],
      [field]: value,
    };
    setCalendars(updated);
  };

  const handleNestedChange = (parent, field, value) => {
    // Helper to set nested value using dot notation
    const setNestedValue = (obj, path, val) => {
      const keys = path.split('.');
      const lastKey = keys.pop();
      const target = keys.reduce((acc, key) => {
        if (!acc[key]) acc[key] = {};
        return acc[key];
      }, obj);
      target[lastKey] = val;
    };

    if (isGlobalView) {
      // Update global config
      setGlobalConfig(prev => {
        const updated = {...prev};
        if (!updated[parent]) updated[parent] = {};
        
        // Handle dot notation in field (e.g., "availability_rules.days")
        if (field.includes('.')) {
          setNestedValue(updated[parent], field, value);
        } else {
          updated[parent][field] = value;
        }
        return updated;
      });
    } else {
      // Update calendar
      const updated = [...calendars];
      const calendar = {...updated[selectedCalendar]};
      if (!calendar[parent]) calendar[parent] = {};
      
      // Handle dot notation in field
      if (field.includes('.')) {
        setNestedValue(calendar[parent], field, value);
      } else {
        calendar[parent][field] = value;
      }
      updated[selectedCalendar] = calendar;
      setCalendars(updated);
    }
  };

  const handleNotificationTemplateChange = (operation, language, channel, value) => {
    setNotificationTemplates((prev) => ({
      ...prev,
      [operation]: {
        ...(prev[operation] || {}),
        [language]: {
          ...(prev[operation]?.[language] || {}),
          [channel]: value,
        },
      },
    }));
  };

  const handleSaveCalendar = async () => {
    try {
      setSaving(true);
      setError(null);

      if (isGlobalView) {
        // Save global settings - this will automatically propagate to calendars without overrides
        // Keep nested structure for global settings
        const updateData = {
          general: globalConfig.general || {
            granularity: 30
          },
          reminders: {
            default_times: globalConfig.reminder_times || []
          },
          meeting_types: globalConfig.meeting_types || {},
          business_hours: globalConfig.business_hours || {
            timezone: 'Europe/Warsaw',
            availability_rules: {}
          },
          booking_settings: globalConfig.booking_settings || {
            scheduling_strategy: globalConfig.scheduling_strategy || 'first_available'
          },
          confirmation: confirmationConfig || {}
        };

        console.log('Saving global settings:', JSON.stringify(updateData, null, 2));

        await businessConfigService.updateGlobalSettings(
          customerId,
          updateData
        );

        // Reload calendars to reflect propagated changes
        const fetchedCalendars = await businessConfigService.getBusinessCalendars(customerId);
        setCalendars(fetchedCalendars);

        setSuccessMessage(t('calendarConfig.alerts.globalSettingsUpdated'));
      } else {
        // Save calendar-specific settings - use nested structure
        const calendarToSave = calendars[selectedCalendar];
        const updateData = {
          general: calendarToSave.general || {
            override_global: false,
            granularity: 30
          },
          // Use nested reminder_settings.times instead of flat reminder_times
          reminder_settings: calendarToSave.reminder_settings || {
            override_global: false,
            times: []
          },
          reminders: calendarToSave.reminders || {
            override_global: false
          },
          meeting_types: calendarToSave.meeting_types || {},
          meeting_types_override: calendarToSave.meeting_types_override || {
            override_global: false
          },
          business_hours: calendarToSave.business_hours || {
            override_global: false,
            timezone: 'Europe/Warsaw',
            availability_rules: {}
          },
          booking_settings: calendarToSave.booking_settings || {},
          notifications: calendarToSave.notifications || {
            override_global: false
          },
          confirmation_config: confirmationConfig || calendarToSave.confirmation_config || {
            override_global: false
          },
          scheduling_strategy: calendarToSave.scheduling_strategy || 'first_available',
        };

        await businessConfigService.updateCalendarConfig(
          customerId,
          calendarToSave.name,
          updateData
        );

        // Save notification templates separately
        if (Object.keys(notificationTemplates).length > 0) {
          await businessConfigService.updateNotificationTemplates(
            customerId,
            calendarToSave.name,
            notificationTemplates
          );
        }

        setSuccessMessage(t('calendarConfig.alerts.calendarUpdated'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Meeting Type Management
  const handleAddMeetingType = () => {
    setMeetingTypeDialog({
      open: true,
      isEdit: false,
      editKey: '',
      name: '',
      duration: '30',
      goal: [],
      description: '',
      goalInput: '',
    });
  };

  const handleEditMeetingType = (key, data) => {
    setMeetingTypeDialog({
      open: true,
      isEdit: true,
      editKey: key,
      name: key,
      duration: data.duration || '30',
      goal: data.goal || [],
      description: data.description || '',
      goalInput: '',
    });
  };

  const handleDeleteMeetingType = (key) => {
    const updated = [...calendars];
    const meetingTypes = { ...updated[selectedCalendar].meeting_types };
    delete meetingTypes[key];
    updated[selectedCalendar] = {
      ...updated[selectedCalendar],
      meeting_types: meetingTypes,
    };
    setCalendars(updated);
  };

  const handleSaveMeetingType = () => {
    const { isEdit, editKey, name, duration, goal, description } = meetingTypeDialog;
    
    if (!name.trim()) {
      setError('Meeting type name is required');
      return;
    }

    const updated = [...calendars];
    const meetingTypes = { ...updated[selectedCalendar].meeting_types };
    
    // Remove old key if editing and name changed
    if (isEdit && editKey !== name) {
      delete meetingTypes[editKey];
    }
    
    meetingTypes[name] = {
      duration,
      goal,
      description,
    };
    
    updated[selectedCalendar] = {
      ...updated[selectedCalendar],
      meeting_types: meetingTypes,
    };
    
    setCalendars(updated);
    setMeetingTypeDialog({ ...meetingTypeDialog, open: false });
  };

  const handleAddGoal = () => {
    const { goalInput, goal } = meetingTypeDialog;
    if (goalInput.trim() && !goal.includes(goalInput.trim())) {
      setMeetingTypeDialog({
        ...meetingTypeDialog,
        goal: [...goal, goalInput.trim()],
        goalInput: '',
      });
    }
  };

  const handleRemoveGoal = (index) => {
    const { goal } = meetingTypeDialog;
    setMeetingTypeDialog({
      ...meetingTypeDialog,
      goal: goal.filter((_, i) => i !== index),
    });
  };

  // Reminder Time Management
  const handleAddReminder = () => {
    setReminderDialog({ open: true, minutes: 60 });
  };

  const handleSaveReminder = () => {
    const { minutes } = reminderDialog;
    
    if (isGlobalView) {
      // Update global settings
      const reminderTimes = [...(globalConfig?.reminders?.default_times || [])];
      
      if (!reminderTimes.includes(minutes) && minutes > 0) {
        reminderTimes.push(minutes);
        reminderTimes.sort((a, b) => a - b);
        
        const updatedGlobal = {
          ...globalConfig,
          reminders: {
            ...globalConfig.reminders,
            default_times: reminderTimes
          }
        };
        setGlobalConfig(updatedGlobal);
      }
    } else {
      // Update calendar - use nested reminder_settings.times
      const updated = [...calendars];
      const reminderSettings = updated[selectedCalendar].reminder_settings || { override_global: false, times: [] };
      const reminderTimes = [...(reminderSettings.times || [])];
      
      if (!reminderTimes.includes(minutes) && minutes > 0) {
        reminderTimes.push(minutes);
        reminderTimes.sort((a, b) => a - b);
        
        updated[selectedCalendar] = {
          ...updated[selectedCalendar],
          reminder_settings: {
            ...reminderSettings,
            times: reminderTimes
          }
        };
        setCalendars(updated);
      }
    }
    
    setReminderDialog({ open: false, minutes: 60 });
  };

  const handleDeleteReminder = (index) => {
    if (isGlobalView) {
      // Update global settings
      const reminderTimes = [...(globalConfig?.reminders?.default_times || [])];
      reminderTimes.splice(index, 1);
      
      const updatedGlobal = {
        ...globalConfig,
        reminders: {
          ...globalConfig.reminders,
          default_times: reminderTimes
        }
      };
      setGlobalConfig(updatedGlobal);
    } else {
      // Update calendar - use nested reminder_settings.times
      const updated = [...calendars];
      const reminderSettings = updated[selectedCalendar].reminder_settings || { override_global: false, times: [] };
      const reminderTimes = [...(reminderSettings.times || [])];
      reminderTimes.splice(index, 1);
      
      updated[selectedCalendar] = {
        ...updated[selectedCalendar],
        reminder_settings: {
          ...reminderSettings,
          times: reminderTimes
        }
      };
      setCalendars(updated);
    }
  };

  const handleBack = () => {
    navigate(ROUTES.HOME);
  };

  if (loading) {
    return <LoadingSpinner message="Loading calendar configurations..." />;
  }

  if (error && calendars.length === 0) {
    return <ErrorDisplay message={error} />;
  }

  if (calendars.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">{t('calendarConfig.noCalendarsFound')}</Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          {t('common.back')}
        </Button>
        <Typography variant="h4" gutterBottom>
          {t('dashboard.calendarSettings')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('dashboard.meetingTypesAndAvailability')}
        </Typography>
      </Box>

      {/* Calendar Selector */}
      <Paper elevation={1} sx={{ mb: 2, p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t('calendarConfig.selectCalendar')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            key={-1}
            label="🌐 Global Settings"
            onClick={() => setSelectedCalendar(-1)}
            color={selectedCalendar === -1 ? 'primary' : 'default'}
            variant={selectedCalendar === -1 ? 'filled' : 'outlined'}
          />
          {calendars.map((cal, index) => (
            <Chip
              key={index}
              label={cal.name || `Calendar ${index + 1}`}
              onClick={() => setSelectedCalendar(index)}
              color={selectedCalendar === index ? 'primary' : 'default'}
              variant={selectedCalendar === index ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Paper>

      <Paper elevation={2}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={t('calendarConfig.tabs.general')} />
          <Tab label={t('calendarConfig.tabs.meetingTypes')} />
          <Tab label={t('calendarConfig.tabs.reminders')} />
          <Tab label={t('calendarConfig.tabs.businessHours')} />
          <Tab label={t('calendarConfig.tabs.bookingSettings')} />
          <Tab label={t('calendarConfig.tabs.notifications')} />
          <Tab label={t('calendarConfig.tabs.confirmation')} />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Show alert if viewing global settings */}
          {isGlobalView && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <span dangerouslySetInnerHTML={{ __html: t('calendarConfig.alerts.editingGlobalSettings') }} />
            </Alert>
          )}
          
          {/* General Settings Tab */}
          {selectedTab === 0 && (
            <Box>
              {/* Override Toggle for General Settings */}
              {!isGlobalView && (
                <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t('calendarConfig.overrideGeneralSettings')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('calendarConfig.overrideGeneralSettingsDescription')}
                      </Typography>
                    </Box>
                    <Switch
                      checked={generalOverride}
                      onChange={(e) => {
                        const enableOverride = e.target.checked;
                        setGeneralOverride(enableOverride);
                        // Update the override_global flag in the calendar's general object
                        handleNestedChange('general', 'override_global', enableOverride);
                        // If disabling override, sync from global
                        if (!enableOverride) {
                          syncFromGlobal('general');
                        }
                      }}
                      color="primary"
                    />
                  </Box>
                </Paper>
              )}
              
            <Grid container spacing={2} sx={{ opacity: (!isGlobalView && !generalOverride) ? 0.6 : 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  disabled
                  label={t('calendarConfig.general.calendarName')}
                  value={currentCalendar.name || ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label={t('calendarConfig.general.timeSlotGranularity')}
                  value={currentCalendar.general?.granularity || 30}
                  onChange={(e) => handleNestedChange('general', 'granularity', parseInt(e.target.value))}
                  disabled={!isGlobalView && !generalOverride}
                  helperText={t('calendarConfig.general.minimumBookingInterval')}
                >
                  {GRANULARITY_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt} {t('booking.minutes')}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            </Box>
          )}

          {/* Meeting Types Tab */}
          {selectedTab === 1 && (
            <Box>
              {/* Override Toggle for Meeting Types */}
              {!isGlobalView && (
                <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t('calendarConfig.meetingTypes.overrideGlobal')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('calendarConfig.meetingTypes.overrideGlobalDescription')}
                      </Typography>
                    </Box>
                    <Switch
                      checked={meetingTypesOverride}
                      onChange={(e) => {
                        const enableOverride = e.target.checked;
                        setMeetingTypesOverride(enableOverride);
                        // Update the override_global flag in meeting_types_override
                        handleNestedChange('meeting_types_override', 'override_global', enableOverride);
                        // If disabling override, sync from global
                        if (!enableOverride) {
                          syncFromGlobal('meeting_types');
                        }
                      }}
                      color="primary"
                    />
                  </Box>
                </Paper>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, opacity: (!isGlobalView && !meetingTypesOverride) ? 0.6 : 1 }}>
                <Typography variant="h6">{t('calendarConfig.meetingTypes.title')}</Typography>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={handleAddMeetingType}
                  disabled={!isGlobalView && !meetingTypesOverride}
                >
                  {t('calendarConfig.meetingTypes.addMeetingType')}
                </Button>
              </Box>
              <List>
                {Object.entries(currentCalendar.meeting_types || {})
                  .filter(([key]) => key !== 'override_global')
                  .map(([key, data]) => (
                  <ListItem key={key} divider>
                    <ListItemText
                      primary={data.description || key}
                      secondary={`${data.duration} ${t('booking.minutes')} | ${data.goal?.join(', ') || t('calendarConfig.meetingTypes.noGoals')}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        onClick={() => handleEditMeetingType(key, data)}
                        disabled={!isGlobalView && !meetingTypesOverride}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        onClick={() => handleDeleteMeetingType(key)}
                        disabled={!isGlobalView && !meetingTypesOverride}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              {Object.keys(currentCalendar.meeting_types || {}).filter(k => k !== 'override_global').length === 0 && (
                <Alert severity="info">{t('calendarConfig.meetingTypes.noMeetingTypes')}</Alert>
              )}
            </Box>
          )}

          {/* Reminders Tab */}
          {selectedTab === 2 && (
            <Box>
              {/* Override Toggle for Reminders */}
              {!isGlobalView && (
                <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t('calendarConfig.overrideReminderSettings')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('calendarConfig.overrideReminderSettingsDescription')}
                      </Typography>
                    </Box>
                    <Switch
                      checked={remindersOverride}
                      onChange={(e) => {
                        const enableOverride = e.target.checked;
                        setRemindersOverride(enableOverride);
                        // Update override flag
                        if (!calendars[selectedCalendar].reminders) {
                          handleNestedChange('reminders', 'override_global', enableOverride);
                        } else {
                          handleNestedChange('reminders', 'override_global', enableOverride);
                        }
                        // If disabling override, sync from global
                        if (!enableOverride) {
                          syncFromGlobal('reminders');
                        }
                      }}
                      color="primary"
                    />
                  </Box>
                </Paper>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">{t('calendarConfig.reminders.title')}</Typography>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={handleAddReminder}
                  disabled={!isGlobalView && !remindersOverride}
                >
                  {t('calendarConfig.reminders.addReminderTime')}
                </Button>
              </Box>
              <List sx={{ opacity: (!isGlobalView && !remindersOverride) ? 0.6 : 1 }}>
                {getReminderTimes().map((minutes, index) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={`${minutes} ${t('calendarConfig.reminders.minutesBefore')}`}
                      secondary={`${Math.floor(minutes / 60)} ${t('calendarConfig.reminders.hours')} ${minutes % 60} ${t('calendarConfig.reminders.minutes')}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        onClick={() => handleDeleteReminder(index)}
                        disabled={!isGlobalView && !remindersOverride}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              {getReminderTimes().length === 0 && (
                <Alert severity="info">{t('calendarConfig.reminders.noReminders')}</Alert>
              )}
            </Box>
          )}

          {/* Business Hours Tab */}
          {selectedTab === 3 && (
            <Box>
              {/* Override Toggle for Business Hours */}
              {!isGlobalView && (
                <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t('calendarConfig.overrideBusinessHours')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('calendarConfig.overrideBusinessHoursDescription')}
                      </Typography>
                    </Box>
                    <Switch
                      checked={businessHoursOverride}
                      onChange={(e) => {
                        const enableOverride = e.target.checked;
                        setBusinessHoursOverride(enableOverride);
                        // Update the override_global flag in business_hours
                        handleNestedChange('business_hours', 'override_global', enableOverride);
                        // If disabling override, sync from global
                        if (!enableOverride) {
                          syncFromGlobal('business_hours');
                        }
                      }}
                      color="primary"
                    />
                  </Box>
                </Paper>
              )}
              
            <Grid container spacing={2} sx={{ opacity: (!isGlobalView && !businessHoursOverride) ? 0.6 : 1 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('calendarConfig.businessHours.operatingDays')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {DAYS_OF_WEEK.map((day) => {
                    const selectedDays = currentCalendar.business_hours?.availability_rules?.days || [];
                    const isSelected = selectedDays.includes(day);
                    const isDisabled = !isGlobalView && !businessHoursOverride;
                    return (
                      <Chip
                        key={day}
                        label={t(`calendarConfig.businessHours.${day}`)}
                        onClick={() => {
                          if (!isDisabled) {
                            const newDays = isSelected
                              ? selectedDays.filter((d) => d !== day)
                              : [...selectedDays, day];
                            handleNestedChange('business_hours', 'availability_rules.days', newDays);
                          }
                        }}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        disabled={isDisabled}
                        sx={{ 
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          opacity: isDisabled ? 0.6 : 1
                        }}
                      />
                    );
                  })}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  label={t('calendarConfig.businessHours.startTime')}
                  value={currentCalendar.business_hours?.availability_rules?.hours_start || '09:00'}
                  onChange={(e) => handleNestedChange('business_hours', 'availability_rules.hours_start', e.target.value)}
                  disabled={!isGlobalView && !businessHoursOverride}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="time"
                  label={t('calendarConfig.businessHours.endTime')}
                  value={currentCalendar.business_hours?.availability_rules?.hours_end || '17:00'}
                  onChange={(e) => handleNestedChange('business_hours', 'availability_rules.hours_end', e.target.value)}
                  disabled={!isGlobalView && !businessHoursOverride}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('calendarConfig.businessHours.timezone')}
                  value={currentCalendar.business_hours?.timezone || 'Europe/Warsaw'}
                  onChange={(e) => handleNestedChange('business_hours', 'timezone', e.target.value)}
                  disabled={!isGlobalView && !businessHoursOverride}
                  helperText={t('calendarConfig.businessHours.businessTimezone')}
                />
              </Grid>
            </Grid>
            </Box>
          )}

          {/* Booking Settings Tab */}
          {selectedTab === 4 && (
            <Box>
              {/* Override Toggle for Booking Settings */}
              {!isGlobalView && (
                <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t('calendarConfig.overrideBookingSettings')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('calendarConfig.overrideBookingSettingsDescription')}
                      </Typography>
                    </Box>
                    <Switch
                      checked={bookingSettingsOverride}
                      onChange={(e) => {
                        const enableOverride = e.target.checked;
                        setBookingSettingsOverride(enableOverride);
                        // Update the override_global flag in booking_settings
                        handleNestedChange('booking_settings', 'override_global', enableOverride);
                        // If disabling override, sync from global
                        if (!enableOverride) {
                          syncFromGlobal('booking_settings');
                        }
                      }}
                      color="primary"
                    />
                  </Box>
                </Paper>
              )}
              
            <Grid container spacing={2} sx={{ opacity: (!isGlobalView && !bookingSettingsOverride) ? 0.6 : 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('calendarConfig.bookingSettings.advanceBookingDays')}
                  value={currentCalendar.booking_settings?.advance_booking_days || 30}
                  onChange={(e) =>
                    handleNestedChange('booking_settings', 'advance_booking_days', parseInt(e.target.value))
                  }
                  helperText={t('calendarConfig.bookingSettings.maximumDaysInAdvance')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('calendarConfig.bookingSettings.minimumNotice')}
                  value={currentCalendar.booking_settings?.min_notice_hours ?? 0}
                  onChange={(e) =>
                    handleNestedChange('booking_settings', 'min_notice_hours', parseInt(e.target.value))
                  }
                  disabled={!isGlobalView && !bookingSettingsOverride}
                  helperText={t('calendarConfig.bookingSettings.minimumNoticeHelp')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('calendarConfig.bookingSettings.bufferTime')}
                  value={currentCalendar.booking_settings?.buffer_time ?? 0}
                  onChange={(e) =>
                    handleNestedChange('booking_settings', 'buffer_time', parseInt(e.target.value))
                  }
                  disabled={!isGlobalView && !bookingSettingsOverride}
                  helperText={t('calendarConfig.bookingSettings.bufferTimeHelp')}
                  InputProps={{ inputProps: { min: 0, step: 5 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label={t('calendarConfig.bookingSettings.allowCancellation')}
                  value={currentCalendar.booking_settings?.allow_cancellation !== false}
                  onChange={(e) =>
                    handleNestedChange('booking_settings', 'allow_cancellation', e.target.value === 'true')
                  }
                  disabled={!isGlobalView && !bookingSettingsOverride}
                >
                  <MenuItem value={true}>{t('common.yes')}</MenuItem>
                  <MenuItem value={false}>{t('common.no')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label={t('calendarConfig.bookingSettings.allowReschedule')}
                  value={currentCalendar.booking_settings?.allow_reschedule !== false}
                  onChange={(e) =>
                    handleNestedChange('booking_settings', 'allow_reschedule', e.target.value === 'true')
                  }
                  disabled={!isGlobalView && !bookingSettingsOverride}
                >
                  <MenuItem value={true}>{t('common.yes')}</MenuItem>
                  <MenuItem value={false}>{t('common.no')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('calendarConfig.bookingSettings.cancellationNotice')}
                  value={currentCalendar.booking_settings?.cancellation_hours || 24}
                  onChange={(e) =>
                    handleNestedChange('booking_settings', 'cancellation_hours', parseInt(e.target.value))
                  }
                  disabled={!isGlobalView && !bookingSettingsOverride}
                  helperText={t('calendarConfig.bookingSettings.hoursBeforeCancellation')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label={t('calendarConfig.bookingSettings.schedulingStrategy')}
                  value={currentCalendar.scheduling_strategy || 'first_available'}
                  onChange={(e) => handleCalendarChange('scheduling_strategy', e.target.value)}
                  disabled={!isGlobalView && !bookingSettingsOverride}
                  helperText={t('calendarConfig.bookingSettings.schedulingStrategyHelp')}
                >
                  <MenuItem value="first_available">{t('calendarConfig.bookingSettings.firstAvailable')}</MenuItem>
                  <MenuItem value="best_fit">{t('calendarConfig.bookingSettings.bestFit')}</MenuItem>
                  <MenuItem value="most_compact">{t('calendarConfig.bookingSettings.mostCompact')}</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            </Box>
          )}

          {/* Notifications Tab */}
          {selectedTab === 5 && (
            <Box>
              {/* Override Toggle for Notifications */}
              {!isGlobalView && (
                <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t('calendarConfig.overrideNotificationTemplates')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('calendarConfig.overrideNotificationTemplatesDescription')}
                      </Typography>
                    </Box>
                    <Switch
                      checked={notificationsOverride}
                      onChange={(e) => {
                        const enableOverride = e.target.checked;
                        setNotificationsOverride(enableOverride);
                        // Update override flag
                        handleNestedChange('notifications', 'override_global', enableOverride);
                        // If disabling override, sync from global
                        if (!enableOverride) {
                          syncFromGlobal('notifications');
                        }
                      }}
                      color="primary"
                    />
                  </Box>
                </Paper>
              )}
              
              <Typography variant="h6" gutterBottom>
                {t('calendarConfig.notifications.title')}
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>{t('calendarConfig.notifications.availablePlaceholders')}</strong>
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  <li><code>{'{date}'}</code> - {t('calendarConfig.notifications.placeholderDate')}</li>
                  <li><code>{'{time}'}</code> - {t('calendarConfig.notifications.placeholderTime')}</li>
                  <li><code>{'{business_name}'}</code> - {t('calendarConfig.notifications.placeholderBusinessName')}</li>
                  <li><code>{'{calendar_name}'}</code> - {t('calendarConfig.notifications.placeholderCalendarName')}</li>
                  <li><code>{'{customer_name}'}</code> - {t('calendarConfig.notifications.placeholderCustomerName')}</li>
                  <li><code>{'{meeting_type}'}</code> - {t('calendarConfig.notifications.placeholderMeetingType')}</li>
                </Box>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  💡 {t('calendarConfig.notifications.smsOptimalLength')}
                </Typography>
              </Alert>

              {/* Language Tabs */}
              <Tabs
                value={notificationLanguage}
                onChange={(e, newValue) => setNotificationLanguage(newValue)}
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label={t('calendarConfig.notifications.polish')} value="pl" disabled={!isGlobalView && !notificationsOverride} />
                <Tab label={t('calendarConfig.notifications.english')} value="en" disabled={!isGlobalView && !notificationsOverride} />
              </Tabs>

              <Box sx={{ opacity: (!isGlobalView && !notificationsOverride) ? 0.6 : 1, pointerEvents: (!isGlobalView && !notificationsOverride) ? 'none' : 'auto' }}>
              {/* Booking Confirmed Templates */}
              <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="success.main">
                  ✓ {t('calendarConfig.notifications.bookingConfirmed')}
                </Typography>
                <Typography variant="caption" display="block" gutterBottom color="text.secondary">
                  {t('calendarConfig.notifications.bookingConfirmedDescription')}
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={t('calendarConfig.notifications.smsTemplate')}
                      value={notificationTemplates.booking_confirmed?.[notificationLanguage]?.sms || ''}
                      onChange={(e) =>
                        handleNotificationTemplateChange('booking_confirmed', notificationLanguage, 'sms', e.target.value)
                      }
                      helperText={`${(notificationTemplates.booking_confirmed?.[notificationLanguage]?.sms || '').length}/160 ${t('common.characters')}`}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={t('calendarConfig.notifications.emailTemplate')}
                      value={notificationTemplates.booking_confirmed?.[notificationLanguage]?.email || ''}
                      onChange={(e) =>
                        handleNotificationTemplateChange('booking_confirmed', notificationLanguage, 'email', e.target.value)
                      }
                      helperText={t('calendarConfig.notifications.emailFutureUse')}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Booking Cancelled Templates */}
              <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="error.main">
                  ✗ {t('calendarConfig.notifications.bookingCancelled')}
                </Typography>
                <Typography variant="caption" display="block" gutterBottom color="text.secondary">
                  {t('calendarConfig.notifications.bookingCancelledDescription')}
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={t('calendarConfig.notifications.smsTemplate')}
                      value={notificationTemplates.booking_cancelled?.[notificationLanguage]?.sms || ''}
                      onChange={(e) =>
                        handleNotificationTemplateChange('booking_cancelled', notificationLanguage, 'sms', e.target.value)
                      }
                      helperText={`${(notificationTemplates.booking_cancelled?.[notificationLanguage]?.sms || '').length}/160 ${t('common.characters')}`}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={t('calendarConfig.notifications.emailTemplate')}
                      value={notificationTemplates.booking_cancelled?.[notificationLanguage]?.email || ''}
                      onChange={(e) =>
                        handleNotificationTemplateChange('booking_cancelled', notificationLanguage, 'email', e.target.value)
                      }
                      helperText={t('calendarConfig.notifications.emailFutureUse')}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Booking Rescheduled Templates */}
              <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="warning.main">
                  ↻ {t('calendarConfig.notifications.bookingRescheduled')}
                </Typography>
                <Typography variant="caption" display="block" gutterBottom color="text.secondary">
                  {t('calendarConfig.notifications.bookingRescheduledDescription')}
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={t('calendarConfig.notifications.smsTemplate')}
                      value={notificationTemplates.booking_rescheduled?.[notificationLanguage]?.sms || ''}
                      onChange={(e) =>
                        handleNotificationTemplateChange('booking_rescheduled', notificationLanguage, 'sms', e.target.value)
                      }
                      helperText={`${(notificationTemplates.booking_rescheduled?.[notificationLanguage]?.sms || '').length}/160 ${t('common.characters')}`}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={t('calendarConfig.notifications.emailTemplate')}
                      value={notificationTemplates.booking_rescheduled?.[notificationLanguage]?.email || ''}
                      onChange={(e) =>
                        handleNotificationTemplateChange('booking_rescheduled', notificationLanguage, 'email', e.target.value)
                      }
                      helperText={t('calendarConfig.notifications.emailFutureUse')}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Booking Reminder Templates */}
              <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold" color="info.main">
                  🔔 {t('calendarConfig.notifications.bookingReminder')}
                </Typography>
                <Typography variant="caption" display="block" gutterBottom color="text.secondary">
                  {t('calendarConfig.notifications.bookingReminderDescription')}
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={t('calendarConfig.notifications.smsTemplate')}
                      value={notificationTemplates.booking_reminder?.[notificationLanguage]?.sms || ''}
                      onChange={(e) =>
                        handleNotificationTemplateChange('booking_reminder', notificationLanguage, 'sms', e.target.value)
                      }
                      helperText={`${(notificationTemplates.booking_reminder?.[notificationLanguage]?.sms || '').length}/160 ${t('common.characters')}`}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label={t('calendarConfig.notifications.emailTemplate')}
                      value={notificationTemplates.booking_reminder?.[notificationLanguage]?.email || ''}
                      onChange={(e) =>
                        handleNotificationTemplateChange('booking_reminder', notificationLanguage, 'email', e.target.value)
                      }
                      helperText={t('calendarConfig.notifications.emailFutureUse')}
                    />
                  </Grid>
                </Grid>
              </Paper>
              </Box>
            </Box>
          )}

          {/* Meeting Confirmation Tab */}
          {selectedTab === 6 && (
            <>
              {/* Override Toggle for Calendar-Specific View */}
              {!isGlobalView && (
                <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {t('calendarConfig.overrideConfirmationSettings')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('calendarConfig.overrideConfirmationSettingsDescription')}
                      </Typography>
                    </Box>
                    <Switch
                      checked={confirmationOverride}
                      onChange={async (e) => {
                        const enableOverride = e.target.checked;
                        setConfirmationOverride(enableOverride);
                        
                        if (!enableOverride) {
                          // If disabling override, sync from global
                          syncFromGlobal('confirmation');
                        } else {
                          // Copy global to calendar for editing and set override flag
                          const updated = [...calendars];
                          const calendar = {...updated[selectedCalendar]};
                          calendar.confirmation_config = {
                            ...(globalConfig?.confirmation || {}),
                            override_global: true
                          };
                          updated[selectedCalendar] = calendar;
                          setCalendars(updated);
                          setConfirmationConfig(calendar.confirmation_config);
                        }
                      }}
                      color="primary"
                    />
                  </Box>
                </Paper>
              )}
              
              <MeetingConfirmationTab
                customerId={customerId}
                calendars={calendars}
                currentCalendarIndex={selectedCalendar}
                confirmationConfig={confirmationConfig}
                setConfirmationConfig={(newConfig) => {
                  setConfirmationConfig(newConfig);
                  // Also update the calendar data for local state
                  if (!isGlobalView && selectedCalendar >= 0) {
                    const updated = [...calendars];
                    updated[selectedCalendar] = {
                      ...updated[selectedCalendar],
                      confirmation_config: {
                        ...newConfig,
                        override_global: confirmationOverride
                      }
                    };
                    setCalendars(updated);
                  }
                }}
                isGlobalView={isGlobalView}
                disabled={!isGlobalView && !confirmationOverride}
              />
            </>
          )}

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="outlined" onClick={handleBack} disabled={saving}>
              {t('calendarConfig.cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveCalendar}
              disabled={saving}
            >
              {saving ? t('common.loading') : t('calendarConfig.saveChanges')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Meeting Type Dialog */}
      <Dialog
        open={meetingTypeDialog.open}
        onClose={() => setMeetingTypeDialog({ ...meetingTypeDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {meetingTypeDialog.isEdit ? t('calendarConfig.meetingTypes.editMeetingType') : t('calendarConfig.meetingTypes.addMeetingTypeDialog')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('calendarConfig.meetingTypes.name')}
                value={meetingTypeDialog.name}
                onChange={(e) =>
                  setMeetingTypeDialog({ ...meetingTypeDialog, name: e.target.value })
                }
                disabled={meetingTypeDialog.isEdit}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label={t('calendarConfig.meetingTypes.duration')}
                value={meetingTypeDialog.duration}
                onChange={(e) =>
                  setMeetingTypeDialog({ ...meetingTypeDialog, duration: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('calendarConfig.meetingTypes.description')}
                multiline
                rows={2}
                value={meetingTypeDialog.description}
                onChange={(e) =>
                  setMeetingTypeDialog({ ...meetingTypeDialog, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t('calendarConfig.meetingTypes.goals')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  label={t('calendarConfig.meetingTypes.addGoal')}
                  value={meetingTypeDialog.goalInput}
                  onChange={(e) =>
                    setMeetingTypeDialog({ ...meetingTypeDialog, goalInput: e.target.value })
                  }
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddGoal();
                    }
                  }}
                />
                <Button variant="outlined" onClick={handleAddGoal}>
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {meetingTypeDialog.goal.map((g, i) => (
                  <Chip
                    key={i}
                    label={g}
                    onDelete={() => handleRemoveGoal(i)}
                    size="small"
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMeetingTypeDialog({ ...meetingTypeDialog, open: false })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSaveMeetingType} variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reminder Dialog */}
      <Dialog
        open={reminderDialog.open}
        onClose={() => setReminderDialog({ ...reminderDialog, open: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('calendarConfig.reminders.dialogTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label={t('calendarConfig.reminders.minutesBeforeAppointment')}
            value={reminderDialog.minutes}
            onChange={(e) =>
              setReminderDialog({ ...reminderDialog, minutes: parseInt(e.target.value) })
            }
            sx={{ mt: 2 }}
            helperText={`${Math.floor(reminderDialog.minutes / 60)} ${t('calendarConfig.reminders.hours')} ${reminderDialog.minutes % 60} ${t('calendarConfig.reminders.minutes')}`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReminderDialog({ ...reminderDialog, open: false })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSaveReminder} variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CalendarConfigPage;
